/**
 * Home-fire landmark — a sacred bonfire on a ring of standing stones placed at
 * each team's spawn. Teaches "this is my safe home base" through a stack of
 * redundant cues (per the respawn-legibility research doc):
 *
 *   1. Six monolith standing stones in a circle — "shrine" silhouette that
 *      reads as a built, owned, permanent place.
 *   2. A central oversized bonfire with a tribe-tinted flame (team identity).
 *      Fire = home is pre-verbal in survival games (Valheim / Minecraft).
 *   3. A glowing ground ring at the stone-circle boundary — the visible
 *      boundary cue that upgrades "landmark" to "sanctuary."
 *   4. A tall vertical light column rising above the fire — cross-map
 *      orientation beacon (Minecraft beacon / Apex dropship).
 *
 * Stone-age low-poly aesthetic: Lambert + applyToonShader noise band on all
 * geometry; additive MeshBasicMaterial on flame cones (self-glow at night).
 * Flame animation mirrors building-render.ts campfire (layered additive cone
 * flicker, sprite halo, point-light intensity modulation).
 *
 * Two tribe palettes:
 *   Team 0 — warm amber/fire tribe (flame reads as hearth)
 *   Team 1 — cool sapphire/spirit tribe (magical blue fire)
 *
 * Usage (from world.ts):
 *   const homeFires = buildHomeFires(spawns, sampleHeight);
 *   scene.add(homeFires.group);
 *   // render loop:
 *   homeFires.update(dt);
 */

import * as THREE from "three";
import { applyToonShader } from "../toon";
import type { TeamSpawns } from "./types";

// ─── tribe identity palettes ─────────────────────────────────────────────────
// World-level team colours — distinct from player-perspective
// TINT_OWN/TINT_ALLY/TINT_ENEMY (which are relative). These are fixed cultural
// colours that identify the two tribes regardless of which team the viewer is on.
const TRIBE_PALETTE = [
  // team 0 — amber fire tribe
  {
    outerCone:  0xff6622 as number,
    midCone:    0xff9a2e as number,
    innerCone:  0xffe27a as number,
    glowSprite: 0xff8a3a as number,
    pointLight: 0xff7a33 as number,
    ring:       0xffaa22 as number,
    column:     0xffcc55 as number,
  },
  // team 1 — sapphire spirit tribe
  {
    outerCone:  0x1155dd as number,
    midCone:    0x3399ff as number,
    innerCone:  0x99ddff as number,
    glowSprite: 0x55aaff as number,
    pointLight: 0x3388ff as number,
    ring:       0x44aaff as number,
    column:     0x66ccff as number,
  },
] as const;

// ─── layout constants ────────────────────────────────────────────────────────
const STONE_RING_RADIUS   = 4.5;   // metres from spawn centre to stone centres
const STONE_COUNT         = 6;
const BONFIRE_SCALE       = 2.0;   // multiplier on the standard campfire size
const BONFIRE_LIGHT_RANGE = 16;    // point-light distance (campfire uses 9)
const BONFIRE_LIGHT_BASE  = 14;    // base intensity (campfire uses 7)
const BEAM_HEIGHT         = 38;    // world metres — tall enough to see cross-map

// Deterministic per-stone height and lean so every placement looks the same
// across clients without Math.random(). Heights feel hand-placed (not uniform).
const STONE_H    = [2.8, 3.3, 2.6, 3.5, 2.9, 3.1] as const;
const STONE_LEAN = [0.06, -0.04, 0.09, -0.06, 0.05, -0.08] as const; // rotation.z

// ─── shared lazy textures ─────────────────────────────────────────────────────

let cachedGlowTex: THREE.Texture | null = null;
/** Radial warm-glow halo sprite (same recipe as campfire in building-render.ts). */
function getGlowTexture(): THREE.Texture {
  if (cachedGlowTex) return cachedGlowTex;
  const size = 64;
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0,   "rgba(255,210,140,0.95)");
  g.addColorStop(0.4, "rgba(255,150,60,0.5)");
  g.addColorStop(1,   "rgba(255,120,40,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  cachedGlowTex = new THREE.CanvasTexture(c);
  return cachedGlowTex;
}

let cachedBeamTex: THREE.Texture | null = null;
/** Vertical gradient for the light-column beam: bright at base, fading at top. */
function getBeamTexture(): THREE.Texture {
  if (cachedBeamTex) return cachedBeamTex;
  const w = 32, h = 128;
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d")!;
  // Horizontal softness: white strip bright at centre, transparent at edges
  const hGrad = ctx.createLinearGradient(0, 0, w, 0);
  hGrad.addColorStop(0,   "rgba(255,255,255,0)");
  hGrad.addColorStop(0.5, "rgba(255,255,255,1)");
  hGrad.addColorStop(1,   "rgba(255,255,255,0)");
  ctx.fillStyle = hGrad;
  ctx.fillRect(0, 0, w, h);
  // Vertical alpha mask: bottom (y=h) = opaque near fire, top (y=0) = transparent
  ctx.globalCompositeOperation = "destination-in";
  const vGrad = ctx.createLinearGradient(0, 0, 0, h);
  vGrad.addColorStop(0,    "rgba(0,0,0,0)");   // top → invisible
  vGrad.addColorStop(0.35, "rgba(0,0,0,0.35)");
  vGrad.addColorStop(1,    "rgba(0,0,0,0.90)"); // bottom → visible
  ctx.fillStyle = vGrad;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = "source-over";
  cachedBeamTex = new THREE.CanvasTexture(c);
  return cachedBeamTex;
}

// ─── per-fire animation handles ───────────────────────────────────────────────

interface FireRefs {
  cones:      THREE.Mesh[];
  glowSprite: THREE.Sprite;
  light:      THREE.PointLight;
  beamPlanes: THREE.Mesh[];
  phaseOff:   number; // time offset so the two fires pulse independently
}

// ─── geometry builders ────────────────────────────────────────────────────────

/** Shared dark-granite stone material — one allocation, shared across all stones. */
function makeStoneMatShared(): THREE.MeshLambertMaterial {
  const mat = new THREE.MeshLambertMaterial({ color: 0x7a7060 });
  applyToonShader(mat, { noiseScale: 0.8 });
  return mat;
}

/**
 * Standing-stone circle: 6 low-poly monolith slabs of varying height, arranged
 * in a ring of radius STONE_RING_RADIUS. Each slab faces inward and leans
 * slightly for the Stonehenge "settled over centuries" look.
 */
function makeStoneRing(
  cx: number, cz: number,
  sampleHeight: (x: number, z: number) => number,
  stoneMat: THREE.MeshLambertMaterial,
): THREE.Group {
  const group = new THREE.Group();
  for (let i = 0; i < STONE_COUNT; i++) {
    // Offset by half-step so no stone sits on the cardinal axes (keeps the
    // silhouette symmetric and avoids hiding a stone directly behind the fire).
    const angle = (i / STONE_COUNT) * Math.PI * 2 + Math.PI / STONE_COUNT;
    const sx = cx + Math.cos(angle) * STONE_RING_RADIUS;
    const sz = cz + Math.sin(angle) * STONE_RING_RADIUS;
    const groundY = sampleHeight(sx, sz);
    const h = STONE_H[i % STONE_H.length]!;
    const geom = new THREE.BoxGeometry(0.65, h, 0.85);
    const stone = new THREE.Mesh(geom, stoneMat);
    // Sink base 0.1u into ground → flush contact, no floating gap
    stone.position.set(sx, groundY + h / 2 - 0.1, sz);
    stone.rotation.y = -angle + Math.PI; // face inward
    stone.rotation.z = STONE_LEAN[i % STONE_LEAN.length]!;
    stone.castShadow  = true;
    stone.receiveShadow = true;
    group.add(stone);
  }
  return group;
}

/**
 * Log pile + stone firepit ring. Positioned as a group so the whole thing can
 * be placed at (cx, groundY, cz) in one call.
 */
function makeLogPile(): THREE.Group {
  const group = new THREE.Group();

  const logMat = new THREE.MeshLambertMaterial({ color: 0x4a2f10 }); // charred wood
  applyToonShader(logMat, { noiseScale: 0.9 });
  // Two crossed logs (X and Z axis), laid flat
  for (let i = 0; i < 2; i++) {
    const geom = new THREE.CylinderGeometry(0.16, 0.22, 2.2, 6);
    const log = new THREE.Mesh(geom, logMat);
    log.rotation.z = Math.PI / 2; // lay flat
    log.rotation.y = (i * Math.PI) / 2; // 0° and 90° → cross pattern
    log.position.y = 0.16; // rest on ground (half the radius above origin)
    log.castShadow = true;
    group.add(log);
  }

  // Sandstone firepit ring around the base of the logs
  const pitGeom = new THREE.TorusGeometry(0.72, 0.19, 5, 10);
  pitGeom.rotateX(Math.PI / 2); // lay flat
  const pitMat = new THREE.MeshLambertMaterial({ color: 0xa09070 });
  applyToonShader(pitMat, { noiseScale: 0.7 });
  const pit = new THREE.Mesh(pitGeom, pitMat);
  pit.position.y = 0.1;
  group.add(pit);

  return group;
}

/**
 * Tribe-tinted bonfire flame. Returns the group (positioned externally) and
 * the animation refs needed by update(). BONFIRE_SCALE makes it visibly larger
 * than a player-built campfire — the landmark should read from across the map.
 */
function makeBonfire(
  palette: (typeof TRIBE_PALETTE)[number],
): { group: THREE.Group; cones: THREE.Mesh[]; glowSprite: THREE.Sprite; light: THREE.PointLight } {
  const group = new THREE.Group();
  const s = BONFIRE_SCALE;

  const coneSpecs = [
    { r: 0.34 * s, h: 0.95 * s, color: palette.outerCone, opacity: 0.80 },
    { r: 0.22 * s, h: 0.78 * s, color: palette.midCone,   opacity: 0.88 },
    { r: 0.12 * s, h: 0.58 * s, color: palette.innerCone, opacity: 0.95 },
  ];
  const cones: THREE.Mesh[] = [];
  for (const spec of coneSpecs) {
    const geom = new THREE.ConeGeometry(spec.r, spec.h, 7);
    const mat = new THREE.MeshBasicMaterial({
      color:      spec.color,
      transparent: true,
      opacity:    spec.opacity,
      blending:   THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    });
    const m = new THREE.Mesh(geom, mat);
    m.position.y = spec.h / 2;
    m.renderOrder = 999;
    group.add(m);
    cones.push(m);
  }

  // Wide warm halo sprite (same glow texture as campfire, larger scale)
  const glowSprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map:        getGlowTexture(),
    color:      palette.glowSprite,
    transparent: true,
    opacity:    0.55,
    blending:   THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  }));
  glowSprite.scale.setScalar(2.4 * s);
  glowSprite.position.y = 0.45 * s;
  group.add(glowSprite);

  // Point light — larger range + intensity than a campfire
  const light = new THREE.PointLight(palette.pointLight, BONFIRE_LIGHT_BASE, BONFIRE_LIGHT_RANGE, 2);
  light.position.y = 0.7 * s;
  group.add(light);

  return { group, cones, glowSprite, light };
}

/**
 * Vertical light-column beacon: two planes crossed at 90° with the beam
 * texture — reads as a pillar of rising light at any camera angle. Using
 * cloned materials so the two fires can pulse with slightly different phase.
 */
function makeBeamColumn(
  palette: (typeof TRIBE_PALETTE)[number],
): { group: THREE.Group; planes: THREE.Mesh[] } {
  const planes: THREE.Mesh[] = [];
  const group  = new THREE.Group();
  for (const angle of [0, Math.PI / 2]) {
    const geom = new THREE.PlaneGeometry(2.2, BEAM_HEIGHT);
    const mat  = new THREE.MeshBasicMaterial({
      map:        getBeamTexture(),
      color:      palette.column,
      transparent: true,
      opacity:    0.20,
      blending:   THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
      side:       THREE.DoubleSide,
    });
    const p = new THREE.Mesh(geom, mat);
    // Centre the plane so it spans from groundY to groundY + BEAM_HEIGHT
    p.position.y  = BEAM_HEIGHT / 2;
    p.rotation.y  = angle;
    p.renderOrder = 997;
    group.add(p);
    planes.push(p);
  }
  return { group, planes };
}

// ─── public API ──────────────────────────────────────────────────────────────

export interface HomeFires {
  group:  THREE.Group;
  update: (dt: number) => void;
}

/**
 * Build both team home-fire landmarks and return a group + update function.
 * The group should be added to the scene by the caller (world.ts). The update
 * function drives flame animation and must be called every frame.
 *
 * Spawn coordinate convention: TeamSpawns.team1/team2 use `{x, y}` where y is
 * world-space Z (consistent with sampleHeight's (x, z) signature).
 *
 *   Team 0 = spawns.twoTeam.team1  (amber tribe)
 *   Team 1 = spawns.twoTeam.team2  (sapphire tribe)
 */
export function buildHomeFires(
  spawns: TeamSpawns,
  sampleHeight: (x: number, z: number) => number,
): HomeFires {
  const group = new THREE.Group();
  group.name  = "home-fires";

  const fireRefs: FireRefs[] = [];
  let elapsed = 0;

  // Shared stone material — same granite across both landmarks for cohesion
  const stoneMat = makeStoneMatShared();

  const entries = [
    { spawn: spawns.twoTeam.team1, teamIdx: 0, phaseOff: 0.00 },
    { spawn: spawns.twoTeam.team2, teamIdx: 1, phaseOff: 1.37 }, // offset so fires pulse out of sync
  ] as const;

  for (const { spawn, teamIdx, phaseOff } of entries) {
    const cx      = spawn.x;
    const cz      = spawn.y;  // TeamSpawns y = world Z
    const groundY = sampleHeight(cx, cz);
    const palette = TRIBE_PALETTE[teamIdx]!;

    // 1. Standing stone ring (each stone samples its own ground height)
    group.add(makeStoneRing(cx, cz, sampleHeight, stoneMat));

    // 3. Log pile + firepit ring — centred at spawn, lifted to groundY
    const logs = makeLogPile();
    logs.position.set(cx, groundY, cz);
    group.add(logs);

    // 4. Tribe-tinted bonfire flame — lifted 0.3u above log bowl
    const { group: flameGroup, cones, glowSprite, light } = makeBonfire(palette);
    flameGroup.position.set(cx, groundY + 0.3, cz);
    group.add(flameGroup);

    // 5. Vertical light-column beacon — base at groundY
    const { group: beamGroup, planes } = makeBeamColumn(palette);
    beamGroup.position.set(cx, groundY, cz);
    group.add(beamGroup);

    fireRefs.push({ cones, glowSprite, light, beamPlanes: planes, phaseOff });
  }

  function update(dt: number): void {
    elapsed += dt;
    for (const f of fireRefs) {
      const t = elapsed + f.phaseOff;
      // Layered sines → organic, non-periodic flicker (same formula as campfire)
      const flick = 0.85 + 0.13 * Math.sin(t * 11) + 0.06 * Math.sin(t * 23.3);
      f.cones.forEach((c, i) => {
        const sway = Math.sin(t * (6 + i * 2) + i) * 0.05;
        c.scale.set(1 + sway, flick + sway, 1 + sway);
        c.rotation.z = Math.sin(t * 3 + i) * 0.05;
      });
      (f.glowSprite.material as THREE.SpriteMaterial).opacity =
        0.42 + 0.12 * Math.sin(t * 9);
      f.light.intensity =
        BONFIRE_LIGHT_BASE * (0.82 + 0.12 * Math.sin(t * 13.7) + 0.06 * Math.sin(t * 27.1));
      // Slow column pulse — atmospheric, not distracting
      const beamOpacity = 0.17 + 0.05 * Math.sin(t * 1.8);
      for (const p of f.beamPlanes) {
        (p.material as THREE.MeshBasicMaterial).opacity = beamOpacity;
      }
    }
  }

  return { group, update };
}
